'use client'

import React, { useMemo } from 'react'
import { motion } from 'motion/react'
import type { Lektionen } from '@/payload-types'
import CustomCardNews from '@/components/CustomCardNews'
import { getImageUrl } from '@/utilities/getImageUrl'
import { CMSLink } from '@/components/Link'
import { formatSlug } from '@/hooks/formatSlug'

interface LessonsGridClientProps {
  lessons: Lektionen[]
  slug: string
}

interface AcademyLectionCard {
  title: string
  description: string
  image: string
  link: string
  key: string
}

export const LessonsGridClient: React.FC<LessonsGridClientProps> = ({ lessons, slug }) => {
  // Helper function to create slug from navText (same as CollectionImageLink)
  const createAcademyLectionSlug = (navText: string | null | undefined): string => {
    if (!navText) return ''
    return formatSlug(navText)
  }

  // Flatten all academyLections from all lessons into cards
  const academyLectionCards = useMemo(() => {
    const cards: AcademyLectionCard[] = []

    lessons.forEach((lesson) => {
      if (
        typeof lesson === 'string' ||
        !lesson.academyLections ||
        lesson.academyLections.length === 0
      ) {
        return
      }

      const lessonSlug = lesson.slug || lesson.id

      lesson.academyLections.forEach((academyLection) => {
        if (!academyLection.navText || !academyLection.title) {
          return
        }

        const academyLectionSlug = createAcademyLectionSlug(academyLection.navText)
        const link = `/academy/${slug}/${lessonSlug}/${academyLectionSlug}`

        cards.push({
          title:
            academyLection.title.trim() || academyLection.navText.trim() || lesson.headline.trim(),
          description: academyLection.introText.trim() || '',
          image: getImageUrl(lesson.thumbnail), // Use parent lesson's thumbnail
          link,
          key: `${lesson.id}-${academyLection.id || academyLection.navText}`,
        })
      })
    })

    return cards
  }, [lessons, slug])

  if (academyLectionCards.length === 0) {
    return null
  }

  return (
    <div className="flex justify-center py-32">
      <div className="container px-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-1 xl:grid-cols-1">
          {academyLectionCards.map((card, idx) => (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{
                delay: idx * 0.1,
                duration: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="flex flex-col items-start justify-between gap-8 border-b pb-4 md:pb-4"
            >
              <div className="flex w-[90%] flex-col gap-2">
                <h3>{card.title}</h3>
                {/* <p className="text-sm text-black md:text-lg">{card.description || ''}</p> */}
              </div>
              <CMSLink
                type="custom"
                url={card.link}
                label="ZUR LEKTION"
                appearance="textlink"
                className="w-full text-sm font-normal uppercase md:items-end md:justify-end md:text-lg"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
